import { useEffect, useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";

import axios from "axios";
import { loadConfig } from "../helpers/config";
import ConvertDate from "../helpers/DateConverter";
import LoadingSpinner from "../components/LoadingSpinner";
import { AuthContext } from "../helpers/AuthContext";

function Home() {
  const { authState } = useContext(AuthContext);
  const [categoryNames, setCategoryNames] = useState({});
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [checks, setChecks] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [apiUrl, setApiUrl] = useState("");
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadingData, setLoadingData] = useState(true);
  const [overdueGroupFilter, setOverdueGroupFilter] = useState("");
  const [upcomingGroupFilter, setUpcomingGroupFilter] = useState("");
  const [defectiveGroupFilter, setDefectiveGroupFilter] = useState("");
  const [inProgressGroupFilter, setInProgressGroupFilter] = useState("");
  const [expandedTools, setExpandedTools] = useState({});

  useEffect(() => {
    async function fetchConfig() {
      const config = await loadConfig();
      setApiUrl(config.APIURL);
      setLoadingConfig(false);
    }
    fetchConfig();
  }, []);

  // Extract unique tools group names for dropdown
  const groupOptions = [
    ...new Set(tools.map(tool => tool.ToolsGroup?.groupname).filter(Boolean))
  ].sort((a, b) => a.localeCompare(b));

  useEffect(() => {
    async function fetchData() {
      if (!loadingConfig) {
        try {
          const [toolsRes, checksRes, toolsgroupsRes, licensesRes] = await Promise.all([
            axios.get(`${apiUrl}/tools`, {
              headers: { accessToken: localStorage.getItem("accessToken") }
            }),
            axios.get(`${apiUrl}/checks`, {
              headers: { accessToken: localStorage.getItem("accessToken") }
            }),
            axios.get(`${apiUrl}/toolsgroups`, {
              headers: { accessToken: localStorage.getItem("accessToken") }
            }),
            ...(authState.canAccessLicenses ? [axios.get(`${apiUrl}/licenses`, {
              headers: { accessToken: localStorage.getItem("accessToken") }
            })] : [Promise.resolve({ data: [] })])
          ]);
          setLicenses(licensesRes.data);
          // Attach categories to each tool's ToolsGroup
          const toolsgroups = toolsgroupsRes.data;
          const toolsWithCategories = toolsRes.data.map(tool => {
            if (tool.ToolsGroup && tool.ToolsGroup.id) {
              const group = toolsgroups.find(g => g.id === tool.ToolsGroup.id);
              tool.ToolsGroup.Categories = group?.Categories || [];
            }
            return tool;
          });
          setTools(toolsWithCategories);
          setChecks(checksRes.data);
        } catch (err) {
          // handle error
        }
        setLoadingData(false);
      }
    }
    fetchData();
  }, [loadingConfig, apiUrl, authState.canAccessLicenses]);

  const today = new Date();
  // Calculate upcoming and overdue checks per category
  const overdueChecks = [];
  const upcomingChecks = [];

  tools.forEach(tool => {
    // Skip defective and discarded tools in overdue/upcoming calculations
    if (tool.isOperational === false || tool.isDiscarded === true) return;

    // Defensive: ensure categories are present
    const categories = Array.isArray(tool.ToolsGroup?.Categories) ? tool.ToolsGroup.Categories : [];
    categories.forEach(category => {
      // Find all checks for this tool and category (exclude invalid checks)
      // Defensive: support both categoryId and CategoryId from backend
      const catChecks = checks.filter(check =>
        check.ToolId === tool.id &&
        (check.categoryId === category.id || check.CategoryId === category.id) &&
        check.isValid !== false
      );
      let latestCheck = null;
      if (catChecks.length > 0) {
        latestCheck = catChecks.reduce((a, b) => new Date(a.checkdate) > new Date(b.checkdate) ? a : b);
      }
      let lastCheckDate = latestCheck ? new Date(latestCheck.checkdate) : null;
      // Calculate nextDueDate based on interval and unit
      let nextDueDate = null;
      const interval = Number(category.interval) || 0;
      const unit = (category.unit || '').toLowerCase();
      function addInterval(date, interval, unit) {
        const d = new Date(date);
        if (unit === 'month' || unit === 'monate') {
          d.setMonth(d.getMonth() + interval);
        } else if (unit === 'year' || unit === 'jahr' || unit === 'jahre') {
          d.setFullYear(d.getFullYear() + interval);
        } else if (unit === 'day' || unit === 'tag' || unit === 'tage') {
          d.setDate(d.getDate() + interval);
        } else {
          // Default to months if unit is missing or unknown
          d.setMonth(d.getMonth() + interval);
        }
        return d;
      }
      if (lastCheckDate && interval > 0) {
        nextDueDate = addInterval(lastCheckDate, interval, unit);
      } else if (tool.dateofpurchase && interval > 0) {
        nextDueDate = addInterval(new Date(tool.dateofpurchase), interval, unit);
      }
      // Only show if category exists and has a name
      if (!category.name) return;
      let isOverdue = false;
      if (!latestCheck) {
        // No check for this category: always overdue
        isOverdue = true;
      } else if (nextDueDate && nextDueDate < today) {
        isOverdue = true;
      }
      const checkRow = {
        ...(latestCheck || {}),
        serialnumber: tool.serialnumber,
        groupname: tool.ToolsGroup?.groupname,
        categoryname: category.name,
        nextDueDate,
        toolId: tool.id,
        Category: category,
        checkdate: latestCheck ? latestCheck.checkdate : null, // Ensure checkdate is always present
        storagePosition: tool.storagePosition || "-"
      };
      if (isOverdue) {
        overdueChecks.push(checkRow);
      } else if (nextDueDate && nextDueDate >= today) {
        upcomingChecks.push(checkRow);
      }
    });
  });

  upcomingChecks.sort((a, b) => new Date(a.nextDueDate) - new Date(b.nextDueDate));
  overdueChecks.sort((a, b) => new Date(a.nextDueDate) - new Date(b.nextDueDate));

  // Calculate due licenses (not entzogen, and no check or last check > 6 months ago)
  const getLicenseNextDue = (license) => {
    if (!license.checks || license.checks.length === 0) return null;
    const lastCheck = [...license.checks].sort(
      (a, b) => new Date(b.check_date) - new Date(a.check_date)
    )[0];
    const nextDue = new Date(lastCheck.check_date);
    nextDue.setMonth(nextDue.getMonth() + 6);
    return nextDue;
  };
  const dueLicenses = licenses
    .filter(license => {
      if (license.status === 'entzogen') return false;
      if (!license.checks || license.checks.length === 0) return true;
      const lastCheck = [...license.checks].sort(
        (a, b) => new Date(b.check_date) - new Date(a.check_date)
      )[0];
      const threshold = new Date();
      threshold.setMonth(threshold.getMonth() - 6);
      return new Date(lastCheck.check_date) < threshold;
    })
    .sort((a, b) => {
      const dueA = getLicenseNextDue(a);
      const dueB = getLicenseNextDue(b);
      if (!dueA && !dueB) return 0;
      if (!dueA) return -1;
      if (!dueB) return 1;
      return dueA - dueB;
    });

  // Map in-progress checks to include groupname and category name from tools
  const inProgressChecks = checks
    .filter(c => c.finished === false)
    .map(check => {
      const tool = tools.find(t => t.id === check.ToolId);
      // Skip checks for discarded tools
      if (!tool || tool.isDiscarded === true) return null;
      return {
        ...check,
        groupname: tool?.ToolsGroup?.groupname || "-",
        storagePosition: tool?.storagePosition || "-",
        categoryId: check.categoryId
      };
    })
    .filter(check => check !== null)
    .sort((a, b) => new Date(a.checkdate) - new Date(b.checkdate));

  // Fetch category names for in-progress checks
  // Track last fetched categoryIds to avoid infinite loop
  const [lastFetchedCategoryIds, setLastFetchedCategoryIds] = useState([]);
  useEffect(() => {
    if (!loadingConfig && inProgressChecks.length > 0 && apiUrl) {
      const uniqueCategoryIds = Array.from(new Set(inProgressChecks.map(c => c.categoryId).filter(Boolean)));
      // Only fetch if categoryIds have changed
      if (JSON.stringify(uniqueCategoryIds) !== JSON.stringify(lastFetchedCategoryIds)) {
        const names = {};
        Promise.all(uniqueCategoryIds.map(async (catId) => {
          try {
            const res = await axios.get(`${apiUrl}/categories/${catId}`, {
              headers: { accessToken: localStorage.getItem("accessToken") }
            });
            names[catId] = res.data.name;
          } catch {
            names[catId] = "-";
          }
        })).then(() => {
          setCategoryNames(names);
          setLastFetchedCategoryIds(uniqueCategoryIds);
        });
      }
    }
  }, [inProgressChecks, apiUrl, loadingConfig, lastFetchedCategoryIds]);

  // Exclude in-progress checks from overdue/upcoming tables
  const inProgressIds = new Set(inProgressChecks.map(c => c.id));
  const filteredOverdueChecks = overdueGroupFilter === "" ? overdueChecks : overdueChecks.filter(check => check.groupname === overdueGroupFilter);
  const filteredUpcomingChecks = upcomingGroupFilter === "" ? upcomingChecks : upcomingChecks.filter(check => check.groupname === upcomingGroupFilter);
  // Remove in-progress checks by id
  const sortedFilteredOverdueChecks = [...filteredOverdueChecks].filter(check => !inProgressIds.has(check.id));
  const sortedFilteredUpcomingChecks = [...filteredUpcomingChecks].filter(check => !inProgressIds.has(check.id));

  // Persistent filters: restore from localStorage on mount
  useEffect(() => {
    const savedOverdue = localStorage.getItem("overdueGroupFilter");
    const savedUpcoming = localStorage.getItem("upcomingGroupFilter");
    const savedDefective = localStorage.getItem("defectiveGroupFilter");
    const savedInProgress = localStorage.getItem("inProgressGroupFilter");
    if (savedOverdue !== null) setOverdueGroupFilter(savedOverdue);
    if (savedUpcoming !== null) setUpcomingGroupFilter(savedUpcoming);
    if (savedDefective !== null) setDefectiveGroupFilter(savedDefective);
    if (savedInProgress !== null) setInProgressGroupFilter(savedInProgress);
  }, []);

  // Save filter changes to localStorage
  const handleOverdueFilterChange = (e) => {
    setOverdueGroupFilter(e.target.value);
    localStorage.setItem("overdueGroupFilter", e.target.value);
  };
  const handleUpcomingFilterChange = (e) => {
    setUpcomingGroupFilter(e.target.value);
    localStorage.setItem("upcomingGroupFilter", e.target.value);
  };
  const handleDefectiveFilterChange = (e) => {
    setDefectiveGroupFilter(e.target.value);
    localStorage.setItem("defectiveGroupFilter", e.target.value);
  };
  const handleInProgressFilterChange = (e) => {
    setInProgressGroupFilter(e.target.value);
    localStorage.setItem("inProgressGroupFilter", e.target.value);
  };


  // Helper to safely format dates
  function safeDate(date) {
    if (!date || date === '-' || isNaN(new Date(date).getTime())) return '-';
    return ConvertDate(date);
  }

  // Group checks by tool
  const groupChecksByTool = (checksArray) => {
    const grouped = {};
    checksArray.forEach(check => {
      const toolId = check.ToolId || check.toolId;
      if (!grouped[toolId]) {
        grouped[toolId] = [];
      }
      grouped[toolId].push(check);
    });
    return grouped;
  };

  // Toggle tool expansion
  const toggleToolExpansion = (toolId, table) => {
    const key = `${table}-${toolId}`;
    setExpandedTools(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (loadingConfig || loadingData) {
    return <LoadingSpinner text="Lade Daten..." />;
  }

  return (
    <div className="container">
      <h1 className="text-center my-4">Hauptwache Wenigmünchen</h1>
      <hr className="divider" />
      <p className="text-center mb-4">Willkommen im Inventar-Management-System der Hauptfeuerwache Wenigmünchen.</p>

      <div className="home-grid">
        <div className="card">
          <div className="card-header">Werkzeuge verwalten</div>
          <div className="card-body">
            <p>Hier können Sie alle Werkzeuge und Ausrüstungsgegenstände einsehen und verwalten.</p>
            <Link to="/tools" className="btn btn-primary">Werkzeuge anzeigen</Link>
          </div>
        </div>

        <div className="card">
          <div className="card-header">Prüfungen durchführen</div>
          <div className="card-body">
            <p>Führen Sie regelmäßige Prüfungen für die Ausrüstung durch und dokumentieren Sie diese.</p>
            <Link to="/checks" className="btn btn-primary">Prüfungen anzeigen</Link>
          </div>
        </div>

        <div className="card">
          <div className="card-header">Werkzeuggruppen</div>
          <div className="card-body">
            <p>Verwalten Sie hier die Werkzeuggruppen für eine bessere Organisation.</p>
            <Link to="/toolsgroups" className="btn btn-primary">Gruppen anzeigen</Link>
          </div>
        </div>

        {authState.canAccessLicenses && (
          <div className="card">
            <div className="card-header">Führerscheinkontrolle</div>
            <div className="card-body">
              <p>Führen Sie die regelmäßige Führerscheinkontrolle der Mitarbeiter durch und dokumentieren Sie diese.</p>
              <Link to="/licenses" className="btn btn-primary">Führerscheine anzeigen</Link>
            </div>
          </div>
        )}
      </div>

      <h2>Prüfungen in Bearbeitung</h2>
      <div className="filter-container">
        <label htmlFor="inProgressGroupFilter">Filter nach Gerätegruppe: </label>
        <select
          id="inProgressGroupFilter"
          className="filter-select"
          value={inProgressGroupFilter}
          onChange={handleInProgressFilterChange}
        >
          <option value="">Alle</option>
          {groupOptions.map((group, idx) => (
            <option key={idx} value={group}>{group}</option>
          ))}
        </select>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Prüfdatum</th>
            <th>Gerät</th>
            <th>Gerätegruppe</th>
            <th>Lagerort</th>
            <th>Prüfkategorie</th>
            <th>Prüfer</th>
            <th>Aktion</th>
          </tr>
        </thead>
        <tbody>
          {(inProgressGroupFilter === "" ? inProgressChecks : inProgressChecks.filter(check => check.groupname === inProgressGroupFilter)).length === 0 ? (
            <tr><td colSpan="7">Keine Prüfungen in Bearbeitung</td></tr>
          ) : (
            (inProgressGroupFilter === "" ? inProgressChecks : inProgressChecks.filter(check => check.groupname === inProgressGroupFilter)).map((check, idx) => (
              <tr key={idx}>
                <td>{ConvertDate(check.checkdate)}</td>
                <td>{check.Tool?.serialnumber || '-'}</td>
                <td>{check.groupname || '-'}</td>
                <td>{check.Tool?.storagePosition || '-'}</td>
                <td>{categoryNames[check.categoryId] || '-'}</td>
                <td>{check.inspector || '-'}</td>
                <td>
                  <Link to={`/Checks/Add/${check.ToolId}`} state={{ checkId: check.id }} className="btn btn-warning" style={{ marginRight: "10px" }}>
                    Fortsetzen
                  </Link>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <h2>Defekte Betriebsmittel</h2>
      <div className="filter-container">
        <label htmlFor="defectiveGroupFilter">Filter nach Gerätegruppe: </label>
        <select
          id="defectiveGroupFilter"
          className="filter-select"
          value={defectiveGroupFilter}
          onChange={handleDefectiveFilterChange}
        >
          <option value="">Alle</option>
          {groupOptions.map((group, idx) => (
            <option key={idx} value={group}>{group}</option>
          ))}
        </select>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Gerät</th>
            <th>Gerätegruppe</th>
            <th>Lagerort</th>
            <th>Eigentümer</th>
          </tr>
        </thead>
        <tbody>
          {tools.filter(tool => tool.isOperational === false && tool.isDiscarded !== true && (defectiveGroupFilter === "" || tool.ToolsGroup?.groupname === defectiveGroupFilter)).length === 0 ? (
            <tr><td colSpan="4">Keine defekten Betriebsmittel</td></tr>
          ) : (
            tools.filter(tool => tool.isOperational === false && tool.isDiscarded !== true && (defectiveGroupFilter === "" || tool.ToolsGroup?.groupname === defectiveGroupFilter)).map((tool, idx) => (
              <tr
                key={idx}
                style={{ cursor: "pointer", backgroundColor: "#ffebee" }}
                onClick={() => navigate(`/tool/${tool.id}`)}
              >
                <td>{tool.serialnumber}</td>
                <td>{tool.ToolsGroup?.groupname || "-"}</td>
                <td>{tool.storagePosition || '-'}</td>
                <td>{tool.owner || '-'}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {authState.canAccessLicenses && (
        <>
          <h2>Überfällige Führerscheinkontrollen</h2>
          {dueLicenses.length === 0 ? (
            <table className="table">
              <tbody><tr><td colSpan="4">Keine überfälligen Führerscheinkontrollen</td></tr></tbody>
            </table>
          ) : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name des Inhabers</th>
                    <th>Führerscheinnummer</th>
                    <th>Letzte Kontrolle</th>
                    <th>Nächste Fälligkeit</th>
                  </tr>
                </thead>
                <tbody>
                  {dueLicenses.map(license => {
                    const lastCheck = license.checks && license.checks.length > 0
                      ? [...license.checks].sort((a, b) => new Date(b.check_date) - new Date(a.check_date))[0]
                      : null;
                    const nextDue = lastCheck
                      ? (() => { const d = new Date(lastCheck.check_date); d.setMonth(d.getMonth() + 6); return d; })()
                      : null;
                    return (
                      <tr
                        key={license.id}
                        style={{ cursor: 'pointer', borderLeft: '4px solid #ffc107', backgroundColor: '#fffbea' }}
                        onClick={() => navigate(`/licenses/${license.id}`)}
                      >
                        <td>{license.holder_name}</td>
                        <td>{license.license_number || (license.encryptedNumber ? '🔒 verschlüsselt' : '—')}</td>
                        <td>{lastCheck ? new Date(lastCheck.check_date).toLocaleDateString('de-DE') : '—'}</td>
                        <td>{nextDue ? nextDue.toLocaleDateString('de-DE') : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <h2>Überfällige Prüfungen</h2>
      <div className="filter-container">
        <label htmlFor="overdueGroupFilter">Filter nach Gerätegruppe: </label>
        <select
          id="overdueGroupFilter"
          className="filter-select"
          value={overdueGroupFilter}
          onChange={handleOverdueFilterChange}
        >
          <option value="">Alle</option>
          {groupOptions.map((group, idx) => (
            <option key={idx} value={group}>{group}</option>
          ))}
        </select>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Nächstes Prüfdatum</th>
            <th>Gerät</th>
            <th>Gerätegruppe</th>
            <th>Lagerort</th>
            <th>Prüfkategorie</th>
            <th>Letzte Prüfung</th>
          </tr>
        </thead>
        <tbody>
          {sortedFilteredOverdueChecks.length === 0 ? (
            <tr><td colSpan="6">Keine überfälligen Prüfungen</td></tr>
          ) : (() => {
            const groupedOverdue = groupChecksByTool(sortedFilteredOverdueChecks);
            const rows = [];
            Object.entries(groupedOverdue)
              .sort(([, aChecks], [, bChecks]) => new Date(aChecks[0].nextDueDate) - new Date(bChecks[0].nextDueDate))
              .forEach(([toolId, toolChecks]) => {
              const hasMultiple = toolChecks.length > 1;
              const isExpanded = expandedTools[`overdue-${toolId}`];
              const firstCheck = toolChecks[0];

              // First row for the tool (or single row if only one check)
              rows.push(
                <tr
                  key={`overdue-${toolId}-main`}
                  style={{ cursor: "pointer", backgroundColor: hasMultiple ? "#f5f5f5" : "transparent" }}
                  onClick={() => hasMultiple ? toggleToolExpansion(toolId, 'overdue') : navigate(`/tool/${toolId}`)}
                >
                  <td>{firstCheck.nextDueDate ? safeDate(firstCheck.nextDueDate) : "-"}</td>
                  <td>
                    {hasMultiple && (
                      <span style={{ marginRight: "5px", fontWeight: "bold" }}>
                        {isExpanded ? "▼" : "►"}
                      </span>
                    )}
                    {firstCheck.serialnumber}
                    {hasMultiple && <span style={{ marginLeft: "5px", color: "#666", fontSize: "0.9em" }}>({toolChecks.length} Prüfungen)</span>}
                  </td>
                  <td>{firstCheck.groupname || "-"}</td>
                  <td>{firstCheck.storagePosition || '-'}</td>
                  <td>{hasMultiple ? "Mehrere" : (firstCheck.Category?.name || firstCheck.categoryname || '-')}</td>
                  <td>{hasMultiple ? "-" : safeDate(firstCheck.checkdate)}</td>
                </tr>
              );

              // Individual rows for all checks if expanded
              if (hasMultiple && isExpanded) {
                toolChecks.forEach((check, idx) => {
                  rows.push(
                    <tr
                      key={`overdue-${toolId}-${idx}`}
                      style={{ cursor: "pointer", backgroundColor: "#fafafa", paddingLeft: "20px" }}
                      onClick={() => navigate(`/tool/${toolId}`)}
                    >
                      <td>{check.nextDueDate ? safeDate(check.nextDueDate) : "-"}</td>
                      <td style={{ paddingLeft: "30px" }}>↳ {check.serialnumber}</td>
                      <td>{check.groupname || "-"}</td>
                      <td>{check.storagePosition || '-'}</td>
                      <td>{check.Category?.name || check.categoryname || '-'}</td>
                      <td>{safeDate(check.checkdate)}</td>
                    </tr>
                  );
                });
              }
            });
            return rows;
          })()}
        </tbody>
      </table>

      <h2>Kommende Prüfungen</h2>
      <div className="filter-container">
        <label htmlFor="upcomingGroupFilter">Filter nach Gerätegruppe: </label>
        <select
          id="upcomingGroupFilter"
          className="filter-select"
          value={upcomingGroupFilter}
          onChange={handleUpcomingFilterChange}
        >
          <option value="">Alle</option>
          {groupOptions.map((group, idx) => (
            <option key={idx} value={group}>{group}</option>
          ))}
        </select>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Nächstes Prüfdatum</th>
            <th>Gerät</th>
            <th>Gerätegruppe</th>
            <th>Lagerort</th>
            <th>Prüfkategorie</th>
            <th>Letzte Prüfung</th>
          </tr>
        </thead>
        <tbody>
          {sortedFilteredUpcomingChecks.length === 0 ? (
            <tr><td colSpan="6">Keine kommenden Prüfungen</td></tr>
          ) : (() => {
            const groupedUpcoming = groupChecksByTool(sortedFilteredUpcomingChecks);
            const rows = [];
            Object.entries(groupedUpcoming)
              .sort(([, aChecks], [, bChecks]) => new Date(aChecks[0].nextDueDate) - new Date(bChecks[0].nextDueDate))
              .forEach(([toolId, toolChecks]) => {
              const hasMultiple = toolChecks.length > 1;
              const isExpanded = expandedTools[`upcoming-${toolId}`];
              const firstCheck = toolChecks[0];

              // First row for the tool (or single row if only one check)
              rows.push(
                <tr
                  key={`upcoming-${toolId}-main`}
                  style={{ cursor: "pointer", backgroundColor: hasMultiple ? "#f5f5f5" : "transparent" }}
                  onClick={() => hasMultiple ? toggleToolExpansion(toolId, 'upcoming') : navigate(`/tool/${toolId}`)}
                >
                  <td>{firstCheck.nextDueDate ? safeDate(firstCheck.nextDueDate) : "-"}</td>
                  <td>
                    {hasMultiple && (
                      <span style={{ marginRight: "5px", fontWeight: "bold" }}>
                        {isExpanded ? "▼" : "►"}
                      </span>
                    )}
                    {firstCheck.serialnumber}
                    {hasMultiple && <span style={{ marginLeft: "5px", color: "#666", fontSize: "0.9em" }}>({toolChecks.length} Prüfungen)</span>}
                  </td>
                  <td>{firstCheck.groupname || "-"}</td>
                  <td>{firstCheck.storagePosition || '-'}</td>
                  <td>{hasMultiple ? "Mehrere" : (firstCheck.Category?.name || firstCheck.categoryname || '-')}</td>
                  <td>{hasMultiple ? "-" : safeDate(firstCheck.checkdate)}</td>
                </tr>
              );

              // Individual rows for all checks if expanded
              if (hasMultiple && isExpanded) {
                toolChecks.forEach((check, idx) => {
                  rows.push(
                    <tr
                      key={`upcoming-${toolId}-${idx}`}
                      style={{ cursor: "pointer", backgroundColor: "#fafafa", paddingLeft: "20px" }}
                      onClick={() => navigate(`/tool/${toolId}`)}
                    >
                      <td>{check.nextDueDate ? safeDate(check.nextDueDate) : "-"}</td>
                      <td style={{ paddingLeft: "30px" }}>↳ {check.serialnumber}</td>
                      <td>{check.groupname || "-"}</td>
                      <td>{check.storagePosition || '-'}</td>
                      <td>{check.Category?.name || check.categoryname || '-'}</td>
                      <td>{safeDate(check.checkdate)}</td>
                    </tr>
                  );
                });
              }
            });
            return rows;
          })()}
        </tbody>
      </table>
    </div>
  );
}

export default Home;
